import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";

import { 
    AgGridCustom as AgGrid,
    CustomSpin as Spin,
    RechartsCustom as Recharts
} from "_components";

import {
    GridContext,
    useApp,
    useAuth,
} from "_contexts";
import { 
    formatHeadernames, 
    isArray,
    isNonEmptyArray,
    shallowCopy,
    hasPermission,
    isEmpty,
    isEmptyArray,
} from "_helpers";
import { 
    useGetColumnDefs,
    useGetReportingCharts,
    useReporting, 
    useReportingGridGroups,
    useReportingDownload,
    useReportingDownloadPdf,
    useCheckboxGroup
} from "_hooks";

import { 
    gridOptions,
    createPinDataReporting,
    createPinDataBilling,
    createPinDataBillingSynt,
} from "./_tableConfigs";

import { TopRequestFormContent as FormContent } from "./TopRequestFormContent";
import { NotificationsModalContent as NotificationsContent } from "./NotificationsModalContent";
import { FiltersDrawerContent as FiltersContent } from "./FiltersDrawerContent";

import styles from "_styles/reporting/reporting.module.css";

import { 
    Badge,
    Button,
    Checkbox,
    Col,
    Divider,
    Drawer,
    Form,
    Menu,
    message as Message,
    Modal,
    Popover,
    Space,
    Row,
} from "antd";

import { useNavigate } from "react-router-dom";

import { ReactComponent as CloseButton } from "_assets/reporting/esc_button.svg";
import { ReactComponent as BillingIcon } from "_assets/reporting/invoice.svg";
import { ReactComponent as Notifications } from "_assets/reporting/notifications.svg";
import { ReactComponent as OptionsPanel } from "_assets/reporting/ext_filters.svg";
import { ReactComponent as ReportingIcon } from "_assets/reporting/report.svg";

import { ReactComponent as Close } from "_assets/close_icon.svg";
import { ReactComponent as Download } from "_assets/download-file.svg";
import { ReactComponent as Cross } from "_assets/xcross_only.svg";
import { ReactComponent as DropdownArrow } from "_assets/dropdown-arrow.svg";
import { ReactComponent as RightArrow } from "_assets/right_arrow.svg";

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const DownloadButton = ({ isDisable, loading, onBtnExport }) => (
    <Button
        type="primary" 
        shape="circle"
        loading={loading}
        danger={isDisable}
        style={isDisable ? {cursor: "not-allowed"} : undefined}
        icon={
            <Download 
                height={20} 
                width={20}
                style={{ display: 'block' }}
            />
        } 
        onClick={() => onBtnExport()}
    />
);

const DownloadPopoverContent = ({ 
    checkboxGroupOptions, 
    checkedList, 
    indeterminate, 
    checkAll, 
    exportIsDisable, 
    onCheckboxGroupAllChange,
    onCheckboxGroupChange
}) => {
        const CheckboxGroup = Checkbox.Group;

        return (
            <>
                <Checkbox
                    indeterminate={indeterminate}
                    onChange={onCheckboxGroupAllChange}
                    checked={checkAll}
                    disabled={exportIsDisable}
                >
                    {
                        checkAll
                        ?
                            'Tout désélectionner'
                        :
                            'Tout sélectionner'
                    }
                </Checkbox>
                <CheckboxGroup
                    options={checkboxGroupOptions}
                    value={checkedList}
                    className={styles['vertical-checkbox-group']}
                    onChange={onCheckboxGroupChange}
                />
            </>
        );
    
};

const NotificationsModalTitle = ({ setNotificationsModalIsOpen }) => (
    <div className="d-flex">
        Notifications
        <span className="ms-auto">
            <Close 
                height={24} 
                width={24}
                style={{
                    cursor: "pointer"
                }}
                onClick={() => setNotificationsModalIsOpen(false)}    
            />
        </span>
    </div>
);

export function Reporting(  ){
    const [ form ] = Form.useForm();

    const basesValue = Form.useWatch('bases', form);
    const detailledReportValue = Form.useWatch('detailled_report', form);
    const displayDatesValue = Form.useWatch('displayDates', form);

    const navigate = useNavigate();

    const { appConfig } = useApp();
    const { user } = useAuth();

    // const leftMenuRef = useRef();
    const userTypeRef = useRef("all");
    const popoverContent = useRef("Clic droit pour réinitialiser les filtres").current;
    const formatFR = useRef("DD/MM/YYYY").current;
    const formatEN = useRef("YYYY-MM-DD").current;
    const defaultOpenedMenu = useRef("reporting").current;
    const defaultSelectedMenu = useRef("reporting_table").current;
    const defaultViewMode = useRef("entity_view").current;
    const initialExternalFiltersState = useRef({
        searchbar: null,
        user: null
    }).current;
    const initialAlertsDisplayedState = useRef({
        localFeeIsMissing: false,
        remoteFeeIsMissing: false,
    }).current;
    const viewModeTranslate = useRef({
        entity_view: 'soc_id',
        account_view: 'acc_id',
        affect_view: 'user',
        record_view: 'record_id'
    }).current;

    const displayDates = useMemo(() => isArray(displayDatesValue) && displayDatesValue.every(date => date) ? displayDatesValue.map(date => dayjs(date).format(formatFR)) : [], [displayDatesValue, formatFR]);

    const getItem = useCallback(( label, key, icon, hidden, children, type ) => ({label, key, icon, hidden, children, type}), []);
    const leftMenuItems = useMemo(() => ([
            getItem('Reporting', 'reporting', <ReportingIcon width={36} height={36}/>, false, [
                getItem('Tableau', 'reporting_table', null, false),
                getItem('Graphiques', 'reporting_charts', null, false),
            ]),
            getItem('Facturation', 'billing', <BillingIcon width={36} height={36}/>, !hasPermission(['reporting', 'reporting_billing'], user), [
                getItem('Encaissements chez Recouvéo', 'billing_int', null, false),
                getItem('Encaissements chez Créancier', 'billing_ext', null, false),
                getItem('Synthèse', 'billing_synt', null, false),
            ])
        ].filter(el => !el.hidden)
    ),[user, getItem]);
    const bases = useMemo(() => {
        if( basesValue === null || basesValue === undefined ) return [];
        if( !isArray(basesValue) ) return [basesValue];
        return basesValue;
    }, [basesValue]);
    const entitiesTranslate = useMemo(() => {
        if( isEmpty(appConfig?.entities_translate) || isEmptyArray(bases) ) return [];
        let tempEntitiesTranslate = [];
        bases.forEach(el => {
            tempEntitiesTranslate = [...tempEntitiesTranslate, ...appConfig.entities_translate[el]];
        })
        return tempEntitiesTranslate;
    }, [appConfig?.entities_translate, bases]);
    const today = dayjs().format('YYYYMMDD_HHmmss');
    const reportingAtToPdf = useMemo(() => {
        if( displayDatesValue === null || displayDatesValue === undefined ) return null;
        if( isEmptyArray(displayDatesValue) ) return null;

        return dayjs(displayDatesValue[1]).format(formatEN);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayDatesValue]);
    
    const [ gridApi, setGridApi ] = useState(null);
    const [ reportingRowDataFiltered, setReportingRowDataFiltered ] = useState([]);
    const [ billingRowDataByViewMode, setBillingRowDataByViewMode ] = useState([]);
    const [ viewMode, setViewMode ] = useState(defaultViewMode);
    const [ currentSelectedMenu, setCurrentSelectedMenu ] = useState(defaultSelectedMenu);
    const [ alertsIsDisplayed, setAlertIsDisplayed ] = useState(initialAlertsDisplayedState);
    const [ externalFilters, setExternalFilter ] = useState(initialExternalFiltersState);

    const [ activeLeftMenuKeyGroup, setActiveLeftMenuKeyGroup ] = useState("reporting");
    const [ notificationsModalIsOpen, setNotificationsModalIsOpen ] = useState(false);
    const [ filtersDrawerIsOpen, setFiltersDrawerIsOpen ] = useState(false);
    const [ attrAreVisible, setAttrAreVisible ] = useState(false);
    const [ assignmentIsVisible, setAssignmentIsVisible ] = useState(false);
    const [ priorityIsVisible, setPriorityIsVisible ] = useState(false);
    const [ menuIsCollapsed, setMenuIsCollapsed ] = useState(false);

    const columsVisibilityConfig = useRef({
        attr: {
            cols: ['atr_propart', 'atr_zonegeo', 'atr_mission'],
            setter: setAttrAreVisible
        },
        assignment: {
            cols: ['assignment'],
            setter: setAssignmentIsVisible
        },
        priority: {
            cols: ['priority'],
            setter: setPriorityIsVisible
        }
    }).current;

    const reportingRowDataFilteredLength = reportingRowDataFiltered.length;

    const pinDataExtraParamsBillingSynt = useMemo(() => viewMode === 'record_view' ? ['amount', 'amount_due'] : [], [viewMode]);

    const { chartsData, chartsColors, chartsPdfData, chartsPdfConfig } = useGetReportingCharts(reportingRowDataFiltered);
    const chartsDataIsNonEmpty = useMemo(() => ( 
        Object.values(chartsData)
            .every(chartData => Object.values(chartData)
            .some(chartValue => chartValue.value !== 0))
        )
    , [chartsData]);

    const { fetchReportingData, fetchReportingToBeChecked, fetchBillingData, exportId, reportingDoRedirect, formIsSubmitted, resetValues, setFormIsSubmitted } = useReporting(form);
    const fetchReportingDataLength = fetchReportingData.length;
    const users = useMemo(() => {
        if( fetchReportingData.length === 0 ) return [];

        return fetchReportingData
            .map(data => data.user)
            .filter((value, index, self) => value && self.indexOf(value) === index)
            .sort()
            .map(user => ({
                value: user,
                label: user
            }));
    }, [fetchReportingData]);
    const exportIsDisable = useMemo(() => {
        if( formIsSubmitted ) return true;
        if( gridApi ){
            return gridApi.getDisplayedRowCount() === 0;
        } else if( currentSelectedMenu === 'reporting_charts' ){
            return !chartsDataIsNonEmpty;
        }
        return false;
    },[gridApi, currentSelectedMenu, formIsSubmitted, chartsDataIsNonEmpty]);
    const checkboxGroupOptions = useMemo(() => ([
        { value: 'columnGroups', label: 'Sans les groupes d\'en-têtes', disabled: exportIsDisable },
        { value: 'skipHeader', label: 'Sans les en-têtes', disabled: exportIsDisable },
        { value: 'skipPinnedTop', label: 'Sans la ligne flottante', disabled: exportIsDisable },
    ]), [exportIsDisable]);
    const clientNameToExport = useMemo(() => {
        if( isEmptyArray(fetchReportingData) || isEmptyArray(entitiesTranslate) ) return null ;

        let uniqueSocIds = fetchReportingData
            .map(data => data.soc_id)
            .filter((value, index, self) => value && self.indexOf(value) === index);

        const entitiesTranslateReduced = entitiesTranslate.reduce((acc, val) => {
            const { soc_id, soc_parent_id } = val;

            if( !uniqueSocIds.includes(soc_id) ) return acc;

            let key = soc_id;

            if( soc_parent_id ) key = soc_parent_id;

            if( !acc[key] ){
                acc[key] = [];
            }

            acc[key].push(val);

            return acc;
        }, {})

        if( Object.keys(entitiesTranslateReduced).length > 1 ){
            return null;  // Case multiple choosen entities
        } else if( Object.keys(entitiesTranslateReduced).length === 1 ){
            const values = Object.values(entitiesTranslateReduced)[0];

            if( values.length > 1 ){
                if( values[0]['soc_parent_id'] ){
                    const findParent = entitiesTranslate.find(entity => entity.soc_id === values[0]['soc_parent_id']);

                    if( findParent !== undefined ){
                        const pattern = /Global/i;
                        return findParent.soc_name.replace(pattern, '').trim();
                    } 
                } else {
                    return values[0]['soc_name'];
                }
            } else if( values.length === 1 ){
                return values[0]['soc_name'];
            } 
        }
    }, [fetchReportingData, entitiesTranslate]);
    const excelCustomParams = useMemo(() => {
        let fileName = '', sheetName = 'Encaissement ', suffix = '';

        if( clientNameToExport ){
            const clientName = clientNameToExport.replaceAll(".", "");
            fileName+= clientName + ' - Encaissement ';
        } else fileName = 'Encaissement ';
        
        switch( currentSelectedMenu ){
            case 'billing_int':
                suffix = 'Recouvéo';
                fileName+= suffix;
                sheetName+= suffix;
                break;
            case 'billing_ext':
                suffix = 'chez vous';
                fileName+= suffix;
                sheetName+= suffix;
                break;
            default : break ;
        }

        if( suffix.trim() ) return {'fileName': fileName, 'sheetName': sheetName};
        return { fileName: 'Reporting - ' + today, sheetName: 'Reporting' };
    }, [currentSelectedMenu, clientNameToExport, today]);
       
    const { columnDefsReporting, columnDefsBillings, columnDefsBillingsSynt } = useGetColumnDefs();
    const { fetchColumnDefs, fetchTableFields, reportingGridGroupsDoRedirect } = useReportingGridGroups(columnDefsReporting);
    const { reportingDownloadDoRedirect, downloadIsPending, initiateDownload } = useReportingDownload(reportingRowDataFiltered);
    const { reportingDownloadPdfDoRedirect, downloadPdfIsPending, initiateDownloadPdf } = useReportingDownloadPdf(reportingRowDataFiltered, detailledReportValue);
    const { checkedList, indeterminate, checkAll, onCheckboxGroupChange, onCheckboxGroupAllChange, onResetAll: resetExportOptions } = useCheckboxGroup(checkboxGroupOptions);

    // Gestion des dates Du...au...
    const colDefsReporting = useMemo(() => {
        if( isEmpty(fetchColumnDefs) ) return [];

        return formatHeadernames(
            fetchColumnDefs, 
            displayDates, 
            fetchReportingDataLength, 
            detailledReportValue, 
            formIsSubmitted
        );
    }, [
        fetchColumnDefs, 
        formIsSubmitted, 
        displayDates, 
        fetchReportingDataLength, 
        detailledReportValue
    ]);
    const filtersAreEnabled = useMemo(() => (
        Object.entries(externalFilters)
            .some(([key, val]) => {
                if( val === null || val === undefined ) return false;
                if( key === "user" ) return val !== "all";
                return val !== "";
            }) ||
            (attrAreVisible || assignmentIsVisible || priorityIsVisible) ||
            viewMode !== defaultViewMode
    ), [externalFilters, viewMode, defaultViewMode, attrAreVisible, assignmentIsVisible, priorityIsVisible]);

    const notifications = useMemo(() => fetchReportingToBeChecked, [fetchReportingToBeChecked]);
    const notificationsCount = notifications.length;
    const leftMenuWidthOpen = 305;
    const leftMenuWidthClose = 100;
    const leftMenuTotalWidth = leftMenuWidthOpen+20;

    useEffect(() => {document.title = "Reporting"}, []);

    useEffect(() => {
        if( reportingDoRedirect || reportingGridGroupsDoRedirect ) navigate('/');
    }, [reportingDoRedirect, reportingGridGroupsDoRedirect, navigate]);

    // FILTRES EXTERNES
    useEffect(() => {
        if( fetchReportingData.length ){
            if( externalFilters.searchbar && externalFilters.user ){
                userTypeRef.current = externalFilters.user;

                if( gridApi ){
                    gridApi.setGridOption('quickFilterText', externalFilters.searchbar);
                    gridApi.onFilterChanged();
                } else {
                    const filteredData = fetchReportingData.filter(data => {
                        const dataValues = Object.values(data).filter(e => e).map(el => el.toString().toLowerCase());

                        const searchbarToLower = externalFilters.searchbar.toLowerCase();
                        const userToLower = externalFilters.user.toLowerCase();

                        return [searchbarToLower, userToLower].every(el => dataValues.includes(el));
                    })

                    setReportingRowDataFiltered(filteredData);
                }
            } else if( externalFilters.searchbar ){
                if( gridApi ){
                    gridApi.setGridOption('quickFilterText', externalFilters.searchbar);
                    gridApi.onFilterChanged();
                } else {
                    const filteredData = fetchReportingData.filter(data => {
                        const dataValues = Object.values(data).filter(e => e).map(el => el.toString().toLowerCase());

                        return dataValues.some(el => el.includes(externalFilters.searchbar.toLowerCase()));
                    })

                    setReportingRowDataFiltered(filteredData);
                }
            } else if( externalFilters.user ){
                userTypeRef.current = externalFilters.user;
        
                if( gridApi ){
                    gridApi.onFilterChanged();
                } else {
                    const filteredData = fetchReportingData.filter(data => {
                        const { user } = data;

                        if( externalFilters.user === 'all' ) return user || user === null;
                        if( externalFilters.user === 'no_affect' ) return user === null;

                        return user && user === externalFilters.user;
                    })

                    setReportingRowDataFiltered(filteredData);
                }
            } else {
                userTypeRef.current = 'all';

                if( gridApi ){
                    gridApi.setGridOption('quickFilterText', '');
                    // gridApi.onFilterChanged();
                } 
                setReportingRowDataFiltered(fetchReportingData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalFilters, currentSelectedMenu, gridApi, fetchReportingData]);

    useEffect(() => {
        if( activeLeftMenuKeyGroup !== 'reporting' && externalFilters.user !== null ){
            setExternalFilter(externalFilters => ({ ...externalFilters, 'user': initialExternalFiltersState.user }));
        }
    }, [activeLeftMenuKeyGroup, externalFilters, initialExternalFiltersState.user]);

    useEffect(() => {  // Gère le colonage
        if( 
            gridApi && 
            currentSelectedMenu === 'billing_synt' 
        ){
            if( isNonEmptyArray(fetchBillingData['LOCAL']) && !alertsIsDisplayed.localFeeIsMissing ){
                const find = fetchBillingData['LOCAL'].findIndex(el => el.rate === 0);

                if( find !== -1 ){
                    Message.error({
                        content: (
                            <>
                                Attention ! L'un des taux de l'onglet <span className="bld-txt">Encaissement chez Recouvéo</span> n'est pas défini
                            </>
                        ),
                        duration: 5
                    });
                    setAlertIsDisplayed(alertsIsDisplayed => ({...alertsIsDisplayed, 'localFeeIsMissing': true}));
                }
            }

            if( isNonEmptyArray(fetchBillingData['REMOTE']) && !alertsIsDisplayed.remoteFeeIsMissing ){
                const find = fetchBillingData['REMOTE'].findIndex(el => el.rate === 0);

                if( find !== -1 ){
                    Message.error({
                        content: (
                            <>
                                Attention ! L'un des taux de l'onglet <span className="bld-txt">Encaissement chez Créancier</span> n'est pas défini
                            </>
                        ),
                        duration: 5
                    });
                    setAlertIsDisplayed(alertsIsDisplayed => ({...alertsIsDisplayed, 'remoteFeeIsMissing': true}));
                }
            }

            let columnDefsBillingsSyntCopy = shallowCopy(columnDefsBillingsSynt);
            const initialValue = {
                headerName: '', children: [
                    { 
                        headerName: "", 
                        width: 50, 
                        lockPosition: "left", 
                        pinned: "left", 
                        headerCheckboxSelection: true, 
                        headerCheckboxSelectionFilteredOnly: true, 
                        checkboxSelection: true,
                        filter: false
                    }
                ]
            };
            let tmpObj = {};
    
            switch( viewMode ){
                case 'entity_view' :
                    tmpObj = {
                        headerName: 'Créancier', children: [
                            { headerName: 'Entité', field: 'soc_id' },
                            { headerName: 'Nom', field: 'soc_name' }
                        ]
                    };
                    break;
                case 'account_view' :
                    tmpObj = {
                        headerName: 'Compte', children: [
                            { headerName: 'N° client', field: 'acc_id' },
                            { headerName: 'Nom', field: 'acc_name' }
                        ]
                    };
                    break;
                case 'affect_view' :
                    tmpObj = {
                        headerName: '', children: [
                            { headerName: 'Affectation', field: 'user' }
                        ]
                    };
                    break;
                case 'record_view' :
                    tmpObj = {
                        headerName: 'Facture', children: [
                            { headerName: 'N° client', field: 'acc_id' },
                            { headerName: 'Nom', field: 'acc_name' },
                            { headerName: 'N° Facture', field: 'record_ref' },
                            { headerName: 'Montant', field: 'amount', type: 'valueColumn' },
                            { headerName: 'Montant dû', field: 'amount_due', type: 'valueColumn' }
                        ]
                    };
                    break;
                default :
                    break;
            }

            columnDefsBillingsSyntCopy = [initialValue, tmpObj, ...columnDefsBillingsSyntCopy];

            gridApi.setGridOption('columnDefs', columnDefsBillingsSyntCopy)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridApi, currentSelectedMenu, viewMode]);

    useEffect(() => {  // Gère le tri de la data
        if( isNonEmptyArray(fetchBillingData['BOTH']) ){
            const  billingRowDataCopy = [...fetchBillingData['BOTH']];
    
            let tempObj = {};

            billingRowDataCopy.forEach((data, idx) => {
                let dataKey = data[viewModeTranslate[viewMode]];

                if( !tempObj[dataKey] ){
                    tempObj[dataKey] = {};
                }

                Object.entries(data).forEach(([key, value]) => {
                    if( typeof value === 'number' ){
                        if( !tempObj[dataKey][key] ){
                            tempObj[dataKey][key] = 0;
                        }
    
                        tempObj[dataKey][key]+= value;
                    } else {
                        if( !tempObj[dataKey][key] ){
                            tempObj[dataKey][key] = '';
                        }

                        tempObj[dataKey][key] = value;
                    }
                })
            })

            setBillingRowDataByViewMode(Object.values(tempObj));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchBillingData, viewMode]);

    // Gère la fermeture du Drawer par la touche echap
    useEffect(() => {
        if( !filtersDrawerIsOpen ) return;

        const handler = (e) => {
            if( e.key === "Escape" || e.code === "Escape" ) {
                e.stopPropagation();       // empêche de remonter au fond
                setFiltersDrawerIsOpen(false)
            }
        };
        const CAPTURE = true;          // intercepte avant les stops en bubble
        window.addEventListener("keydown", handler, CAPTURE);
        return () => window.removeEventListener("keydown", handler, CAPTURE);
    }, [filtersDrawerIsOpen]);

    const updateRowData = useCallback(( newData ) => {
        setReportingRowDataFiltered(newData);
    }, []);

    const handleToggleColumns = useCallback(( key, forceDisable = false ) => {
        const config = columsVisibilityConfig[key];
        if( !config ) return ;

        const { setter, cols } = config;
        setter(prevVal => {
            const newVal = forceDisable ? false : !prevVal;
            gridApi?.applyColumnState({
                state: cols.map((colId) => ({ colId, hide: !newVal })),
            });
            return newVal;
        });
    }, [gridApi, columsVisibilityConfig]);

    const resetAdditionalsColumns = useCallback(( ) => {
        const forceDisable = true;
        Object.keys(columsVisibilityConfig).forEach( el => handleToggleColumns(el, forceDisable) );
    }, [columsVisibilityConfig, handleToggleColumns]);

    const resetFilters = useCallback(( ) => {
        setExternalFilter(initialExternalFiltersState);
        setViewMode(defaultViewMode);
        resetAdditionalsColumns();
    }, [initialExternalFiltersState, defaultViewMode, resetAdditionalsColumns]);

    // PRIMARY FORM  
    const resetFields = useCallback(async() => {    
        setReportingRowDataFiltered([]);
        setBillingRowDataByViewMode([]);
        setAlertIsDisplayed(initialAlertsDisplayedState);

        if( gridApi ) gridApi.setGridOption('quickFilterText', "");
        resetFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialAlertsDisplayedState, gridApi, resetFilters]);

    const isExternalFilterPresent = ( ) => activeLeftMenuKeyGroup === 'reporting' && userTypeRef.current !== 'all';
    const doesExternalFilterPass = ( node ) => {
        const userRef = userTypeRef.current;
        const userNode = node.data.user;

        if( userRef ){
            if( !['all', 'no_affect'].includes(userRef) ) return userRef === userNode;
            if( userRef === 'no_affect' ) return userNode === '' || userNode === null || userNode === undefined;
        }
        return true;
    };

    const toggleRenderComponent = (  ) => {
        switch( currentSelectedMenu ){
            case 'reporting_table' :
                return (
                    <AgGrid 
                        columnDefs={colDefsReporting}
                        fetchData={fetchReportingData}
                        loading={formIsSubmitted}
                        createPinData={createPinDataReporting}
                        createPinDataExtraParams={fetchTableFields}
                        setGridApi={setGridApi}
                        updateRowData={updateRowData}
                    />
                ) ;
            case 'reporting_charts' :
                return (
                    <>
                        {
                            chartsDataIsNonEmpty 
                            ?
                                <>
                                    <Recharts 
                                        id="gen-chart"
                                        fetchData={chartsData.gen_chart} 
                                        fetchColors={chartsColors.gen_chart} 
                                    />
                                    <Recharts 
                                        id="oth-chart"
                                        fetchData={chartsData.oth_chart} 
                                        fetchColors={chartsColors.oth_chart} 
                                    />
                                    <Recharts 
                                        id="ant-chart"
                                        fetchData={chartsData.ant_chart} 
                                        fetchColors={chartsColors.ant_chart} 
                                    />
                                </>
                            :
                                formIsSubmitted 
                                ?
                                    <Spin />
                                :
                                    <div className="d-flex align-items-center justify-content-center h-100 w-100">
                                        Veuillez lancer une requête sur l'onglet&nbsp;<span className="bld-txt">Tableau</span>, aucunes données à afficher pour le moment
                                    </div>
                        }
                    </>
                ) ;
            case 'billing_int' :
                return (
                    <AgGrid 
                        columnDefs={columnDefsBillings}
                        fetchData={fetchBillingData['LOCAL']}
                        loading={formIsSubmitted}
                        setGridApi={setGridApi}
                        createPinData={createPinDataBilling}
                    />
                ) ;
            case 'billing_ext' :
                return (
                    <AgGrid 
                        columnDefs={columnDefsBillings}
                        fetchData={fetchBillingData['REMOTE']}
                        loading={formIsSubmitted}
                        setGridApi={setGridApi}
                        createPinData={createPinDataBilling}
                    />
                ) ;
            case 'billing_synt' :
                return (
                    <AgGrid 
                        columnDefs={columnDefsBillingsSynt}
                        fetchData={billingRowDataByViewMode}
                        loading={formIsSubmitted}
                        setGridApi={setGridApi}
                        createPinData={createPinDataBillingSynt}
                        createPinDataExtraParams={pinDataExtraParamsBillingSynt}
                    />
                ) ;
            
            default :
                return ;
        }
    };

    const requiredMarkRenderer = ( label, { required } ) => {
        return (
            <>
                { label }
                { required && <span className="red-txt">*</span> }
            </>
        )
    };

    // EXCEL EXPORT
    const rowsAreSelected = (  ) => {
        return isNonEmptyArray(gridApi?.getSelectedRows() ?? []);
    };

    const getParams = (  ) => {
        const columnsToExport = gridApi.getColumns()
            .filter(el => el.colDef.headerName !== '' && el.visible !== false);

        return {
            skipColumnGroupHeaders: checkedList.includes("columnGroups"),
            skipColumnHeaders: checkedList.includes("skipHeader"),
            skipPinnedTop: checkedList.includes("skipPinnedTop"),
            onlySelected: rowsAreSelected(),
            columnKeys: columnsToExport,
            author: 'Veostack',
            processCellCallback: ( params ) => {
                const value = params.value;
                const colId = params.column.colId;
                const column = params.api.getColumn(colId);
                const colIndex = params.api.getAllDisplayedColumns().indexOf(column);

                if ( params.node.rowPinned && colIndex === 1 ) {
                    return 'Total: ';
                }
                return value;
            },
            ...excelCustomParams
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }
    const onBtnExport = () => {
        if( exportIsDisable ) return ;
        
        const displayedRowCount = gridApi.getDisplayedRowCount();
        if( displayedRowCount > 250000 ){
            // envoi par mail avec demande d'adresse email
            // return ;
        }

        const fetchReportingDataCount = fetchReportingData.length; // Toute la data disponible au fetch initial

        if( detailledReportValue && currentSelectedMenu === 'reporting_table' ) { // Fichier personnalisé Seulement sur la grid Reporting
            let filters = {};
            let selectedRows = gridApi.getSelectedRows();
            const selectedRowsCount = selectedRows.length;

            if( selectedRowsCount > 0 && selectedRowsCount !== fetchReportingDataCount ){ // Certaines lignes affichées ont été sélectionnées
                let selectedRowsDisplayed = [];
                gridApi.forEachNodeAfterFilter(node => {
                    if( !node.group && node.isSelected() ) {
                        selectedRowsDisplayed.push({ 
                            acc_id: node.data.acc_id, 
                            record_id: node.data.record_id 
                        })
                    }
                });
             
                filters['selectedRows'] = selectedRowsDisplayed;
            } else if( selectedRowsCount === 0 && displayedRowCount !== fetchReportingDataCount ) { // Pas de sélection mais filtres actifs
                let selectedRowsDisplayed = [];
                gridApi.forEachNodeAfterFilter(node => {
                    if( !node.group ) {
                        selectedRowsDisplayed.push({ 
                            acc_id: node.data.acc_id, 
                            record_id: node.data.record_id 
                        })
                    }
                });
             
                filters['selectedRows'] = selectedRowsDisplayed;
            }

            initiateDownload(basesValue, clientNameToExport, reportingAtToPdf);
        } else { // Géré par l'export d'AG grid
            gridApi.exportDataAsExcel(getParams());
        }
    };
    const onBtnExportPdf = () => {
        if( exportIsDisable ) return ;
        initiateDownloadPdf(basesValue, clientNameToExport, reportingAtToPdf);
    }

    return(
        <>
            <Drawer
                title="Filtres"
                placement="left"
                width={leftMenuTotalWidth}
                open={filtersDrawerIsOpen}
                keyboard={true}
                // mask={false}
                closable={false}
            >
                <Button
                    className={styles.edgeClose}
                    type="primary"
                    shape="circle"
                    icon={<CloseButton />}
                    style={{
                        width: 48,
                        height: 48,
                        fontSize: 30,
                        zIndex: 1
                    }}
                    onClick={() => setFiltersDrawerIsOpen(false)}
                />
                <FiltersContent 
                    activeLeftMenuKeyGroup={activeLeftMenuKeyGroup}
                    currentSelectedMenu={currentSelectedMenu}
                    externalFilters={externalFilters}
                    gridApi={gridApi} 
                    users={users} 
                    viewMode={viewMode}
                    defaultViewMode={defaultViewMode}
                    attrAreVisible={attrAreVisible}
                    assignmentIsVisible={assignmentIsVisible}
                    priorityIsVisible={priorityIsVisible}
                    handleToggleColumns={handleToggleColumns}
                    setActiveLeftMenuKeyGroup={setActiveLeftMenuKeyGroup}
                    setExternalFilter={setExternalFilter}
                    setViewMode={setViewMode}
                    setAttrAreVisible={setAttrAreVisible}
                    setAssignmentIsVisible={setAssignmentIsVisible}
                    setPriorityIsVisible={setPriorityIsVisible}
                />
            </Drawer>
            <Modal 
                title={<NotificationsModalTitle setNotificationsModalIsOpen={setNotificationsModalIsOpen} />}
                open={notificationsModalIsOpen}
                closable={false}
                centered={false}
                footer={null}
                width={600}
                style={{
                    top: 56,
                    right: 24,
                    position: "absolute",
                    margin: 0
                }}
                onCancel={() => setNotificationsModalIsOpen(false)}
            >
                <NotificationsContent fetchData={notifications}/>
            </Modal>
            <Form
                onFinish={async() => {
                    await resetFields();
                    setFormIsSubmitted(true);
                }}
                className="mb-0 d-flex justify-content-center"
                requiredMark={requiredMarkRenderer}
                form={form}
                layout="inline"
                initialValues={{
                    detailled_report: false,
                    bases: [],
                    entities: []
                }}
            >
                <FormContent 
                    selectedLeftMenu={currentSelectedMenu} 
                    reportingDataLength={reportingRowDataFilteredLength} 
                    resetValues={resetValues} 
                    resetFields={resetFields}
                />
            </Form>
            <Divider className="d-flex mx-auto align-items-center mt-0 mb-3" style={{width: '80%', minWidth: '0%'}}>
                <Space>
                    {
                        filtersAreEnabled
                        ?
                            <Badge 
                                classNames={{ root: styles.badgeRoot }}
                                count={
                                    <Popover content={popoverContent} placement="right">
                                        <span 
                                            className={styles.badgeCountWrapper}
                                            onContextMenu={(e) => {
                                                e.preventDefault()
                                                resetFilters();
                                            }}
                                        >
                                            <Cross height={20} width={20} />
                                        </span>
                                    </Popover>
                                } 
                            >
                                <Button 
                                    type="primary"
                                    shape="circle"
                                    icon={
                                        <OptionsPanel 
                                            height={20} 
                                            width={20}
                                            viewBox="0 0 24 24"
                                            style={{ 
                                                display: 'block', 
                                                paddingTop: '1px' 
                                            }}
                                        />
                                    }
                                    onClick={() => setFiltersDrawerIsOpen(prevValue => !prevValue)}
                                />
                            </Badge>
                        :
                            <Button 
                                type="primary"
                                shape="circle"
                                icon={
                                    <OptionsPanel 
                                        height={20} 
                                        width={20}
                                        viewBox="0 0 24 24"
                                        style={{ 
                                            display: 'block', 
                                            paddingTop: '1px' 
                                        }}
                                    />
                                }
                                onClick={() => setFiltersDrawerIsOpen(prevValue => !prevValue)}
                            />
                    }

                    {
                        gridApi && !detailledReportValue
                        ?
                            <Popover
                                title="Paramètres d'export"
                                content={
                                    <DownloadPopoverContent 
                                        checkboxGroupOptions={checkboxGroupOptions} 
                                        checkedList={checkedList} 
                                        indeterminate={indeterminate} 
                                        checkAll={checkAll} 
                                        exportIsDisable={exportIsDisable} 
                                        onCheckboxGroupAllChange={onCheckboxGroupAllChange}
                                        onCheckboxGroupChange={onCheckboxGroupChange}
                                    />
                                }
                                trigger="contextMenu" 
                            >
                                <span>
                                    <DownloadButton 
                                        isDisable={exportIsDisable}
                                        loading={downloadIsPending}
                                        onBtnExport={onBtnExport}
                                    />
                                </span>
                            </Popover>
                        :
                        <DownloadButton 
                            isDisable={exportIsDisable}
                            loading={gridApi ? downloadIsPending : downloadPdfIsPending}
                            onBtnExport={gridApi ? onBtnExport : onBtnExportPdf}
                        />
                    }
                    
                    {
                        notificationsCount>0
                        ?
                            <Badge count={notificationsCount} offset={[-8,0]}>
                                <Button 
                                    type="primary" 
                                    shape="circle"
                                    icon={
                                        <Notifications 
                                            height={20} 
                                            width={20}
                                            style={{ display: 'block' }}
                                        />
                                    } 
                                    onClick={() => setNotificationsModalIsOpen(prevValue => !prevValue)}
                                />
                            </Badge>
                        :
                            null
                    }
                </Space>
            </Divider>
            <Row style={{height: 'calc(100vh - 240px)'}} gutter={16}>
                <Col 
                    className={`${styles.menuCol} ${menuIsCollapsed ? styles.isCollapsed : ''}`}
                    flex={menuIsCollapsed ? leftMenuWidthClose+"px" : leftMenuWidthOpen+"px"} 
                >
                    <Button
                        className={menuIsCollapsed ? styles.toggleLeftMenuClosed : styles.toggleLeftMenuOpen}
                        type="primary"
                        shape="circle"
                        icon={<RightArrow height={20} width={20} viewBox="0 0 50 50" style={{ display: 'block' }}/>}
                        onClick={() => setMenuIsCollapsed(prevValue => !prevValue)}
                    />
                    <Menu
                        className="h-100"
                        defaultSelectedKeys={[defaultSelectedMenu]}
                        defaultOpenKeys={[defaultOpenedMenu]}
                        mode="inline"
                        items={leftMenuItems}
                        inlineCollapsed={menuIsCollapsed}
                        expandIcon={({ isOpen }) => <DropdownArrow transform={isOpen ? "rotate(180)" : undefined} className="expand-icon"/>}
                        onSelect={({ key }) => {
                            setCurrentSelectedMenu(key);
                            if( key === 'reporting_charts' ) setGridApi(null);
                        }}
                    />
                </Col>
                <Col flex={1}>
                    <GridContext.Provider value={{ gridOptions, isExternalFilterPresent, doesExternalFilterPass }}>
                        { toggleRenderComponent() }
                    </GridContext.Provider>
                </Col>
            </Row>
        </>
    );
}